<template>
    <div class="running-bco-card" :class="{ expanded: expanded }">
        <!-- CARD HEADER -->
        <div class="running-bco-header" @click="$emit('toggle')">
            <q-icon :name="expanded ? 'expand_more' : 'chevron_right'" size="16px" color="grey-7" />

            <div class="running-bco-title">
                <div class="running-bco-title-row">
                    <div class="running-bco-name" :title="bco.name">{{ bco.name }}</div>
                    <div class="running-bco-chip">BCO Type: {{ bco.type === 'agent-recommended' ? 'Agent-recommended' : 'Approved' }}</div>
                </div>
                <div class="running-bco-subtitle running-bco-meta">
                    <span v-if="bco.strategic_imperative" class="running-bco-si" :title="bco.strategic_imperative">{{ bco.strategic_imperative }}</span>
                    <span v-if="bco.strategic_imperative" class="q-mx-xs">·</span>
                    <span class="running-bco-counts">{{ countsLabel }}</span>
                </div>
            </div>

            <!-- Fixed-width middle column keeps every card's pipeline status lined up under one another. -->
            <div class="running-bco-status">
                <q-spinner v-if="isRunning" color="accent" size="14px" class="q-mr-xs" />
                <div class="running-bco-chip" :class="statusChipClass">Pipeline Status: {{ statusLabel }}</div>
            </div>

            <!-- Both slots are fixed-width and the action slot is kept even without a button, so the
                 MLR chips start at the same point on every card. -->
            <div class="running-bco-end">
                <div class="running-bco-mlr">
                    <div v-if="showMlrChip" class="running-bco-chip" :class="mlrStatus.chipClass">MLR: {{ mlrStatus.label }}</div>
                </div>
                <div class="running-bco-action">
                    <div v-if="showStartedDetails" class="running-bco-started">
                        <div class="running-bco-started-line" :title="startedAtLabel"><span class="running-bco-started-key">Started At</span>{{ startedAtLabel }}</div>
                        <div class="running-bco-started-line" :title="startedByLabel"><span class="running-bco-started-key">Started By</span>{{ startedByLabel }}</div>
                    </div>
                    <q-btn v-else-if="canStartActivity" unelevated no-caps size="sm" text-color="white" class="running-bco-action-btn q-py-none" style="background: #2e7d46;" :loading="bco.starting_activity" @click.stop="$emit('start-activity')">Start</q-btn>
                </div>
            </div>
        </div>

        <!-- CARD BODY -->
        <div v-if="expanded" class="q-pa-sm">
            <!-- AGENT THOUGHT TRACE panel -->
            <div class="running-bco-panel">
                <div class="flex full-width justify-between items-center">
                    <div style="color: #5f5f5f; font-size: 10px; font-weight: 700; letter-spacing: 1px;">AGENT THOUGHT TRACE</div>

                    <div class="flex items-center">
                        <div v-if="bco.trace_complete && !isFailed" class="text-italic q-mr-sm" style="color: #acacac; font-size: 10px;">{{ bco.visible_steps.length }} step{{ bco.visible_steps.length === 1 ? '' : 's' }} · complete</div>
                        <q-btn v-if="bco.visible_steps.length" outline no-caps size="sm" color="grey-7" class="q-py-none q-px-sm bg-white" style="border-radius: 6px; font-size: 10px;" @click="$emit('toggle-trace')">{{ bco.show_trace ? 'Hide trace' : 'Show trace' }}</q-btn>
                    </div>
                </div>

                <div v-if="isWaitingForFirstStep" class="flex items-center q-mt-sm">
                    <q-spinner color="accent" size="16px" class="q-mr-sm" />
                    <div style="font-size: 11px; color: #5f5f5f;">Agent is starting up...</div>
                </div>

                <NBAThoughtTrace v-else-if="bco.show_trace" class="q-mt-sm" :data="bco.visible_steps" :running="!bco.trace_complete" />

                <div v-if="isFailed" class="q-mt-sm" style="color: #b33700; font-size: 11px;">
                    <q-icon name="error_outline" size="14px" class="q-mr-xs" />This run did not finish. Trigger it again from the Strategize page.
                </div>
            </div>

            <!-- SAMPLE DECISIONS panel -->
            <div v-if="bco.run_complete && !isFailed" class="running-bco-panel q-mt-sm">
                <div v-if="!bco.loading_decisions && (bco.veeva_header || bco.veeva_message)" class="veeva-content">
                    <div class="flex full-width justify-between items-center">
                        <div class="veeva-content-label">VEEVA HEADER &amp; MESSAGE</div>
                        <q-btn v-if="canSendForMlrApproval" unelevated no-caps size="sm" text-color="white" class="q-py-none q-px-sm q-mr-sm" style="border-radius: 6px; font-size: 10px; background: #1f5fa8;" :loading="bco.sending_for_mlr" @click="$emit('send-for-mlr-approval')">Send for MLR Approval</q-btn>
                    </div>
                    <div v-if="bco.veeva_header" class="q-mt-xs" style="font-size: 12px; font-weight: 600; color: #242424;">{{ bco.veeva_header }}</div>
                    <div v-if="bco.veeva_message" class="q-mt-xs" style="font-size: 11px; color: #5f5f5f; line-height: 1.5; white-space: pre-line;">{{ bco.veeva_message }}</div>
                </div>

                <div class="flex full-width justify-between items-center q-mb-sm">
                    <div style="color: #5f5f5f; font-size: 10px; font-weight: 700; letter-spacing: 1px;">SAMPLE RECOMMENDATIONS</div>
                    <div v-if="bco.sample_decisions.length" class="text-italic" style="color: #acacac; font-size: 10px;">{{ bco.sample_decisions.length }} of {{ bco.total_decisions }} recommendations explained</div>
                </div>

                <div v-if="bco.loading_decisions" class="flex full-width items-center q-py-sm">
                    <q-spinner color="accent" size="16px" class="q-mr-sm" />
                    <div style="font-size: 11px; color: #5f5f5f;">Loading recommendations...</div>
                </div>

                <template v-else-if="bco.sample_decisions.length">
                    <div class="decision-row decision-row--head">
                        <div>HCP</div>
                        <div>REP</div>
                        <div>CHANNEL</div>
                        <div>EXPLAINABILITY</div>
                        <div>RECOMMENDATION BLOCKED REASON</div>
                        <div></div>
                    </div>

                    <div v-for="(decision, index) in decisionsOnPage" :key="`decision-${bco.bco_id}-${decisionPage}-${index}`" class="decision-row" :class="{ 'decision-row--blocked': isBlocked(decision) }">
                        <div>
                            <div style="font-size: 12px; font-weight: 700;">{{ decision.hcp_name }}</div>
                            <div class="q-mt-xs" style="font-size: 10px; color: #858585;">{{ decision.hcp_id }}</div>
                        </div>

                        <div>
                            <div style="font-size: 11px; color: #414141;">{{ decision.rep_name }}</div>
                            <div class="q-mt-xs" style="font-size: 10px; color: #858585;">{{ decision.territory }}</div>
                        </div>

                        <div>
                            <div class="channel-chip" :class="{ 'channel-chip--muted': !isRteChannel(decision.channel) }">{{ decision.channel }}</div>
                        </div>

                        <div class="decision-explainability" v-html="renderInlineMarkdown(decision.explainability)"></div>

                        <div class="decision-exclusion-reason">{{ decision.decision_exclusion_reason || '—' }}</div>

                        <div class="flex justify-end items-start">
                            <AgentTrustScoreButton :scores="storedAgentTrustScores(decision.agent_eval_score)" empty-message="Agent trust scores have not been generated for this explanation."/>
                        </div>
                    </div>

                    <div v-if="decisionPageCount > 1" class="decision-pagination">
                        <div style="color: #858585; font-size: 10px;">{{ decisionRangeLabel }}</div>
                        <q-pagination v-model="decisionPage" :max="decisionPageCount" :max-pages="5" boundary-numbers direction-links flat color="grey-7" active-color="grey-8" size="10px" gutter="2px" />
                    </div>
                </template>

                <div v-else class="flex full-width q-py-sm" style="font-size: 11px; color: #5f5f5f;">No decisions with a message and an explanation were produced for this objective.</div>
            </div>
        </div>
    </div>
</template>

<script>
import NBAThoughtTrace from '@/components/NBAThoughtTrace.vue';
import AgentTrustScoreButton from '@/components/AgentTrustScoreButton.vue';
import { storedAgentTrustScores } from '@/agentTrustScores.js';

const DECISIONS_PER_PAGE = 5;

const MLR_STATUSES = {
    send_for_approval: { label: 'Send for Approval', chipClass: 'running-bco-chip--info' },
    waiting_for_approval: { label: 'Waiting for Approval', chipClass: 'running-bco-chip--pending' },
    approved: { label: 'Approved', chipClass: 'running-bco-chip--done' },
    rejected: { label: 'Rejected', chipClass: 'running-bco-chip--failed' },
};

export default {
    name: 'RunningBcoCard',

    components: {
        NBAThoughtTrace,
        AgentTrustScoreButton,
    },

    props: {
        bco: {
            type: Object,
            required: true,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
    },

    // A card is remounted when its run moves from the running list to the completed one, so
    // whether the trace is open is held by the objective rather than by the card.
    emits: ['toggle', 'toggle-trace', 'send-for-mlr-approval', 'start-activity'],

    data() {
        return {
            decisionPage: 1,
        };
    },

    watch: {
        // A run that finishes while its card is open swaps the decisions in underneath the reader.
        'bco.sample_decisions'() {
            this.decisionPage = 1;
        },
    },

    computed: {
        decisionsOnPage() {
            const start = (this.decisionPage - 1) * DECISIONS_PER_PAGE;
            return this.bco.sample_decisions.slice(start, start + DECISIONS_PER_PAGE);
        },

        decisionPageCount() {
            return Math.ceil(this.bco.sample_decisions.length / DECISIONS_PER_PAGE);
        },

        decisionRangeLabel() {
            const start = (this.decisionPage - 1) * DECISIONS_PER_PAGE + 1;
            return `${start}-${start + this.decisionsOnPage.length - 1} of ${this.bco.sample_decisions.length}`;
        },

        statusLabel() {
            const label = (this.bco.running_status || '').replace(/_/g, ' ');
            return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
        },

        isRunning() {
            return this.bco.running_status === 'running';
        },

        isFailed() {
            return this.bco.running_status === 'failed';
        },

        // An approved run reaches the workbench before the agent has written its first step, so
        // the panel says it is working rather than showing an empty trace.
        isWaitingForFirstStep() {
            return this.bco.show_trace && !this.bco.visible_steps.length && !this.bco.run_complete;
        },

        // A re-run replaces the decisions of the previous one, so their count only means
        // something again once the agent has written them.
        countsLabel() {
            const hcps = `${this.bco.hcp_count} HCPs`;
            return this.bco.run_complete && !this.isFailed ? `${hcps} · ${this.bco.decision_count} valid recommendations this week` : hcps;
        },

        mlrStatus() {
            return MLR_STATUSES[String(this.bco.mlr_status || '').trim().toLowerCase()] || null;
        },

        // An approval only stands for decisions a run has finished producing.
        showMlrChip() {
            if (!this.mlrStatus) {
                return false;
            }
            const isApproved = String(this.bco.mlr_status || '').trim().toLowerCase() === 'approved';
            return !(isApproved && (this.isRunning || this.isFailed));
        },

        // An approved BCO is started by its run completing, so its start only means something on a
        // completed run.
        showStartedDetails() {
            if (!this.bco.started_at) {
                return false;
            }
            return this.bco.type === 'agent-recommended' || this.bco.running_status === 'completed';
        },

        canStartActivity() {
            return this.bco.type === 'agent-recommended'
                && this.bco.running_status === 'completed'
                && String(this.bco.mlr_status || '').trim().toLowerCase() === 'approved';
        },

        startedAtLabel() {
            const startedAt = new Date(this.bco.started_at);
            if (Number.isNaN(startedAt.getTime())) {
                return '';
            }
            return startedAt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
        },

        startedByLabel() {
            const label = this.bco.started_by || '';
            return label
                .split(/\s+/)
                .filter(Boolean)
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        },

        canSendForMlrApproval() {
            return this.bco.type === 'agent-recommended' && String(this.bco.mlr_status || '').trim().toLowerCase() === 'send_for_approval';
        },

        statusChipClass() {
            if (this.isFailed) {
                return 'running-bco-chip--failed';
            }
            return this.bco.running_status === 'completed' ? 'running-bco-chip--done' : '';
        },
    },

    methods: {
        storedAgentTrustScores,

        isBlocked(decision) {
            return Boolean(String(decision.decision_exclusion_reason || '').trim());
        },

        isRteChannel(channel) {
            return String(channel || '').trim().toUpperCase() === 'RTE';
        },

        renderInlineMarkdown(text) {
            if (!text) {
                return '';
            }

            const renderInline = (line) => line
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;')
                .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

            const html = [];
            let listOpen = false;

            String(text).split(/\r?\n/).forEach((line) => {
                const bullet = line.match(/^\s*[-*]\s+(.+)$/);

                if (bullet) {
                    if (!listOpen) {
                        html.push('<ul>');
                        listOpen = true;
                    }
                    html.push(`<li>${renderInline(bullet[1])}</li>`);
                    return;
                }

                if (listOpen) {
                    html.push('</ul>');
                    listOpen = false;
                }

                if (line.trim()) {
                    html.push(`<div>${renderInline(line.trim())}</div>`);
                }
            });

            if (listOpen) {
                html.push('</ul>');
            }

            return html.join('');
        },
    },
};
</script>

<style scoped>
.running-bco-card {
    width: 100%;
    margin-top: 8px;
    border: 1px solid #dedede;
    border-radius: 8px;
    background: #ffffff;
    transition: all 0.1s ease;
}

.running-bco-card.expanded {
    border: 1.5px solid #ff4e00;
}

.running-bco-header {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) 200px minmax(max-content, 1fr);
    column-gap: 12px;
    align-items: center;
    width: 100%;
    padding: 10px 16px;
    cursor: pointer;
    border-radius: 8px;
}

.running-bco-card:not(.expanded) .running-bco-header:hover {
    background: #f7f7f7;
}

.running-bco-card.expanded .running-bco-header {
    background: #fff1ea;
    border-bottom: 1px solid #ffd3c0;
    border-radius: 6px 6px 0 0;
}

.running-bco-title {
    min-width: 0;
}

.running-bco-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.running-bco-name {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 13px;
    font-weight: 700;
}

.running-bco-title-row .running-bco-chip {
    flex-shrink: 0;
}

.running-bco-subtitle {
    display: flex;
    align-items: center;
    min-width: 0;
    margin-top: 2px;
    white-space: nowrap;
}

.running-bco-si {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

.running-bco-counts {
    flex-shrink: 0;
}

.running-bco-status {
    display: flex;
    align-items: center;
    justify-content: center;
}

.running-bco-end {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
}

.running-bco-mlr {
    display: flex;
    width: 160px;
    flex-shrink: 0;
}

.running-bco-action {
    display: flex;
    justify-content: flex-end;
    width: 180px;
    flex-shrink: 0;
}

.running-bco-started {
    width: 100%;
    min-width: 0;
    font-size: 10px;
    color: #414141;
    line-height: 1.4;
}

.running-bco-started-line {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.running-bco-started-key {
    display: inline-block;
    width: 58px;
    color: #858585;
}

.running-bco-action-btn {
    min-width: 56px;
    border-radius: 6px;
    font-size: 10px;
}

.running-bco-meta {
    font-size: 10px;
    color: #858585;
}

.running-bco-chip {
    padding: 2px 6px;
    border-radius: 10px;
    background: #ffe1d3;
    color: #b33700;
    font-size: 9px;
    font-weight: 700;
    white-space: nowrap;
}

.running-bco-chip--pending {
    background: #fff4c2;
    color: #8a6a00;
}

.running-bco-chip--info {
    background: #dcebfb;
    color: #1f5fa8;
}

.running-bco-chip--done {
    background: #dff0e3;
    color: #2e7d46;
}

.running-bco-chip--failed {
    background: #fbdcdc;
    color: #b02020;
}

.running-bco-panel {
    width: 100%;
    padding: 10px 16px 12px;
    border: 1px solid #dedede;
    border-radius: 6px;
    background: #fdfdfd;
}

.veeva-content {
    position: relative;
    width: 100%;
    margin-bottom: 10px;
    padding: 10px 12px;
    border: 1px solid #ffd3c0;
    border-radius: 6px;
    background: #fff8f4;
}

.veeva-content-label {
    color: #9c9c9c;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
}

.decision-explainability :deep(ul) {
    margin: 2px 0 0;
    padding-left: 16px;
}

.decision-explainability :deep(li) {
    margin-top: 2px;
}

.decision-row {
    display: grid;
    grid-template-columns: 1.1fr 0.8fr 0.5fr 2.7fr 1fr 24px;
    gap: 16px;
    width: 100%;
    padding: 12px 8px;
    border-top: 1px solid #ececec;
}

.decision-row--blocked {
    background: #fdecec;
}

.decision-row--head {
    padding: 6px 8px;
    border-top: none;
    border-bottom: 1px solid #ececec;
    color: #9c9c9c;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1px;
}

.decision-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-top: 8px;
    border-top: 1px solid #ececec;
}

.channel-chip {
    display: inline-block;
    padding: 2px 8px;
    border: 1px solid #ffd3c0;
    border-radius: 10px;
    background: #fff1ea;
    color: #b33700;
    font-size: 10px;
    font-weight: 600;
}

.channel-chip--muted {
    border-color: #dadada;
    background: #f0f0f0;
    color: #5f5f5f;
}

.decision-explainability {
    font-size: 11px;
    color: #5f5f5f;
    line-height: 1.5;
}

.decision-explainability :deep(strong) {
    color: #242424;
}

.decision-exclusion-reason {
    font-size: 11px;
    color: #5f5f5f;
    line-height: 1.5;
}
</style>
